import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Calculator, Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSettings, whatsappHref } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/fiyat-hesaplama", label: "Fiyat Hesaplama" },
  { to: "/calismalarimiz", label: "Çalışmalarımız" },
  { to: "/blog", label: "Blog" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/iletisim", label: "İletişim" },
];

export const PublicLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-ink-100" data-testid="site-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive ? "text-brand-700 bg-brand-50" : "text-ink-700 hover:text-brand-600 hover:bg-brand-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/fiyat-hesaplama" className="hidden sm:block">
              <Button data-testid="header-price-btn" className="gradient-primary text-white rounded-full font-semibold hover:brightness-110 hover:-translate-y-0.5 transition-transform">
                <Calculator className="h-4 w-4 mr-2" /> Fiyat Al
              </Button>
            </Link>
            <a
              data-testid="header-whatsapp-btn"
              href={whatsappHref(settings.whatsapp_number, "Merhaba, mobilya hakkında bilgi almak istiyorum.")}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold shadow-sm hover:brightness-110 transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-700"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menüyü aç"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-ink-100 bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm font-semibold ${
                      isActive ? "text-brand-700 bg-brand-50" : "text-ink-700"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/fiyat-hesaplama" className="mt-2">
                <Button className="w-full gradient-primary text-white rounded-full font-semibold">
                  <Calculator className="h-4 w-4 mr-2" /> Fiyat Al
                </Button>
              </Link>
              <a
                href={whatsappHref(settings.whatsapp_number)}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-3 text-sm font-semibold"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp'tan Yaz
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 gradient-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-4 gap-10">
          <div>
            <Logo chip="none" size="lg" />
            <p className="mt-4 text-sm text-white/70 leading-relaxed">
              Ölçüye özel mobilya tasarımı · kaliteli malzeme, hızlı teklif, WhatsApp üzerinden kolay sipariş.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-brand-300 mb-4">Hızlı Linkler</h4>
            <ul className="space-y-2 text-sm text-white/80">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-brand-300 transition-colors">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-brand-300 mb-4">İletişim</h4>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-brand-300" /> {settings.phone || "+90 533 508 93 24"}</li>
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-brand-300" /> {settings.email || "info@malatyamobilyatasarim.com"}</li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-brand-300" /> {settings.address || "Malatya, Türkiye"}</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest text-brand-300 mb-4">Bize Ulaşın</h4>
            <a
              href={whatsappHref(settings.whatsapp_number)}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold shadow-md hover:brightness-110 transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp'tan Yaz
            </a>
            <div className="mt-5 flex gap-3">
              {settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-brand-500 transition-colors"><Instagram className="h-5 w-5" /></a>}
              {settings.facebook && <a href={settings.facebook} target="_blank" rel="noreferrer" className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-brand-500 transition-colors"><Facebook className="h-5 w-5" /></a>}
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/60">
          © {new Date().getFullYear()} Malatya Mobilya Tasarım. Tüm hakları saklıdır. ·{" "}
          <Link to="/admin/login" className="hover:text-brand-300">Yönetim Paneli</Link>
        </div>
      </footer>

      <WhatsAppFloat />
    </div>
  );
};
